import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_HEALTHDISTRICTS_LOAD,
    ACTION_HEALTHDISTRICTS_LOAD_BY_ID,
    ACTION_HEALTHDISTRICTS_LOAD_FAILURE,
    ACTION_HEALTHDISTRICTS_LOAD_SUCCESS,

    ACTION_HEALTHDISTRICTS_SAVE,
    ACTION_HEALTHDISTRICTS_SAVE_SUCCESS,
    ACTION_HEALTHDISTRICTS_SAVE_FAILURE,

    ACTION_HEALTHDISTRICTS_CREATE,
    ACTION_HEALTHDISTRICTS_CREATE_SUCCESS,
    ACTION_HEALTHDISTRICTS_CREATE_FAILURE,

    ACTION_HEALTHDISTRICTS_VALIDATE,
    ACTION_HEALTHDISTRICTS_VALIDATE_SUCCESS,
    ACTION_HEALTHDISTRICTS_VALIDATE_FAILURE,

    ACTION_HEALTHDISTRICTS_CLEAR,
    ACTION_HEALTHDISTRICTS_FILTER,

    HealthDistrictAction
} from 'src/actions/healthDistrict';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IHealthDistrictState {
    formData: Dtos.HealthDistrict | Dtos.HealthDistrict[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.HealthDistrict[];
    formLength: number;
}

const initialState: IHealthDistrictState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IHealthDistrictState, _isLoading: boolean): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IHealthDistrictState,
    formData: Dtos.HealthDistrict | Dtos.HealthDistrict[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IHealthDistrictState, formFilter: IFormFilter): IHealthDistrictState {

    const healthDistricts: Dtos.HealthDistrict[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = healthDistricts ? healthDistricts.length : 0;

    const formFilteredData = healthDistricts ? healthDistricts.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IHealthDistrictState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function loadFailure(state: IHealthDistrictState, responseStatus: Dtos.ResponseStatus): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IHealthDistrictState, propertiesToClear?: string[]): IHealthDistrictState {

    if (!propertiesToClear) {
        const newState: IHealthDistrictState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IHealthDistrictState, _isSaving: boolean): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IHealthDistrictState,
    formData: Dtos.HealthDistrict | Dtos.HealthDistrict[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IHealthDistrictState, responseStatus: Dtos.ResponseStatus): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IHealthDistrictState, _isCreating: boolean): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IHealthDistrictState,
    formData: Dtos.HealthDistrict,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IHealthDistrictState, responseStatus: Dtos.ResponseStatus): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IHealthDistrictState, _isValidating: boolean): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IHealthDistrictState,
    formData: Dtos.HealthDistrict,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IHealthDistrictState, responseStatus: Dtos.ResponseStatus): IHealthDistrictState {
    const newState: IHealthDistrictState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const HealthDistrictReducer: Reducer<IHealthDistrictState> = (state: IHealthDistrictState = initialState, action: HealthDistrictAction) => {
    switch (action.type) {
        case ACTION_HEALTHDISTRICTS_LOAD:
        case ACTION_HEALTHDISTRICTS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_HEALTHDISTRICTS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_HEALTHDISTRICTS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_HEALTHDISTRICTS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_HEALTHDISTRICTS_SAVE:
            return saving(state, true);
        case ACTION_HEALTHDISTRICTS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_HEALTHDISTRICTS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_HEALTHDISTRICTS_CREATE:
            return creating(state, true);
        case ACTION_HEALTHDISTRICTS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_HEALTHDISTRICTS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_HEALTHDISTRICTS_VALIDATE:
            return validating(state, true);
        case ACTION_HEALTHDISTRICTS_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_HEALTHDISTRICTS_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_HEALTHDISTRICTS_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default HealthDistrictReducer;